/**
 * Posting rules (M15-32–M15-39): list, editor, Test (simulation) and the
 * Draft → Submitted → Approved → Active lifecycle.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, FlaskConical, Plus, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { dateTime, peso, shortDate } from '../../lib/format';
import { manilaToday, useAccounts, type Account } from '../../lib/queries';
import { Alert, Button, Card, EmptyState, Field, FilterBar, PageHeader, Skeleton, TableWrap, Td, Th, filterClass, inputClass } from '../../components/ui';
import { ReasonDialog } from '../../components/ReasonDialog';
import { useEventTypes } from './EventTypesPage';
import { useMappingTables } from './MappingTablesPage';
import { RuleStatusChip, errorText, type RuleStatus } from './shared';

type Selector = { kind: 'FIXED'; code: string } | { kind: 'ROLE'; role: string } | { kind: 'FIELD'; expr: string } | { kind: 'MAPPING'; table: string; keys: Record<string, string> };
interface LineTemplate { side: 'DEBIT' | 'CREDIT'; account: Selector; amount: string; description?: string; forEach?: string; dimensions?: Record<string, string> }
interface Version { id: string; version: number; status: RuleStatus; effectiveFrom: string; conditions: string[]; lines: LineTemplate[]; createdById: string; submittedById: string | null; approvedById: string | null; activatedAt: string | null; retiredAt: string | null; lastSimulation: { passed: boolean; createdAt: string; payload?: unknown } | null; createdAt: string }
interface Rule { id: string; eventType: string; name: string; priority: number; createdAt: string; versions: Version[] }
interface SimLine { accountCode: string; accountName: string; debit: string; credit: string; description: string; dimensionLabels: Record<string, string> }
interface SimResult { passed: boolean; code: string | null; message: string; lines: SimLine[]; totalDebit?: string; totalCredit?: string; errors?: { field: string; message: string }[] }

export function useRules() {
  return useQuery({ queryKey: ['posting-rules'], queryFn: () => api<Rule[]>('/posting-rules') });
}

const SYSTEM_ROLES = ['OUTPUT_VAT', 'INPUT_VAT', 'EWT_PAYABLE', 'CASH_ON_HAND', 'UNDEPOSITED', 'SALES_DEFAULT', 'SALES_DISCOUNTS', 'SC_PWD_DISCOUNTS', 'SALES_RETURNS'];

function selectorLabel(s: Selector): string {
  if (s.kind === 'FIXED') return s.code;
  if (s.kind === 'ROLE') return `role ${s.role}`;
  if (s.kind === 'FIELD') return `field ${s.expr}`;
  return `${s.table}(${Object.entries(s.keys).map(([k, v]) => `${k}=${v}`).join(', ')})`;
}

export function PostingRulesPage(): JSX.Element {
  const { can } = useAuth();
  const rules = useRules();
  const types = useEventTypes();
  const [eventType, setEventType] = useState('');
  const shown = (rules.data ?? []).filter((r) => !eventType || r.eventType === eventType);
  return (
    <>
      <PageHeader title="Posting Rules" description="How each event becomes a balanced journal. Changes are new versions, simulated and approved before use." actions={can('rules.edit') && <Link to="/integrations/rules/new"><Button><Plus aria-hidden="true" className="h-4 w-4" /> New rule</Button></Link>} />
      <FilterBar>
        <select className={filterClass} value={eventType} onChange={(e) => setEventType(e.target.value)} aria-label="Event type">
          <option value="">All event types</option>
          {types.data?.map((t) => <option key={t.code} value={t.code}>{t.code}</option>)}
        </select>
      </FilterBar>
      {rules.isLoading && <Skeleton className="h-40 w-full" />}
      {rules.data && shown.length === 0 && <Card><EmptyState title="No posting rules" description="An event with no active rule waits in the exception queue — it is never posted to suspense." /></Card>}
      {shown.length > 0 && (
        <Card>
          <TableWrap>
            <thead><tr><Th>Rule</Th><Th>Event type</Th><Th align="right">Priority</Th><Th>Versions</Th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {shown.map((r) => (
                <tr key={r.id}>
                  <Td><Link className="font-semibold text-brand-700 hover:underline dark:text-brand-100" to={`/integrations/rules/${r.id}`}>{r.name}</Link></Td>
                  <Td><code className="text-xs">{r.eventType}</code></Td>
                  <Td align="right" className="tabular-nums">{r.priority}</Td>
                  <Td>
                    <div className="flex flex-wrap gap-2">
                      {r.versions.map((v) => (
                        <span key={v.id} className="inline-flex items-center gap-1 text-xs"><span className="font-semibold">v{v.version}</span> <RuleStatusChip status={v.status} /> <span className="text-slate-500">from {shortDate(v.effectiveFrom)}</span></span>
                      ))}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </Card>
      )}
    </>
  );
}

function LineEditor({ line, accounts, tables, onChange, onRemove, onMove, readOnly }: { line: LineTemplate; accounts: Account[]; tables: { code: string; keyFields: string[] }[]; onChange: (l: LineTemplate) => void; onRemove: () => void; onMove: (d: -1 | 1) => void; readOnly: boolean }): JSX.Element {
  const sel = line.account;
  const setKind = (kind: Selector['kind']) => {
    if (kind === 'FIXED') onChange({ ...line, account: { kind, code: '' } });
    else if (kind === 'ROLE') onChange({ ...line, account: { kind, role: 'OUTPUT_VAT' } });
    else if (kind === 'FIELD') onChange({ ...line, account: { kind, expr: '' } });
    else onChange({ ...line, account: { kind, table: tables[0]?.code ?? '', keys: Object.fromEntries((tables[0]?.keyFields ?? []).map((k) => [k, `item.${k}`])) } });
  };
  const dims = Object.entries(line.dimensions ?? {}).map(([k, v]) => `${k}=${v}`).join('; ');
  return (
    <fieldset disabled={readOnly} className="grid gap-2 rounded-xl border border-slate-200 p-3 dark:border-slate-800 md:grid-cols-12">
      <label className="md:col-span-2"><span className="text-xs font-medium text-slate-500">Side</span>
        <select className={filterClass + ' w-full'} value={line.side} onChange={(e) => onChange({ ...line, side: e.target.value as LineTemplate['side'] })}><option value="DEBIT">Debit</option><option value="CREDIT">Credit</option></select>
      </label>
      <label className="md:col-span-3"><span className="text-xs font-medium text-slate-500">Account from</span>
        <select className={filterClass + ' w-full'} value={sel.kind} onChange={(e) => setKind(e.target.value as Selector['kind'])}>
          <option value="FIXED">A fixed account</option><option value="MAPPING">A mapping table</option><option value="ROLE">A system role</option><option value="FIELD">A payload field (account code)</option>
        </select>
      </label>
      <div className="md:col-span-7">
        <span className="text-xs font-medium text-slate-500">Account</span>
        {sel.kind === 'FIXED' && (
          <select className={filterClass + ' w-full'} value={sel.code} onChange={(e) => onChange({ ...line, account: { kind: 'FIXED', code: e.target.value } })}>
            <option value="">Choose…</option>
            {accounts.filter((a) => a.isPosting && a.active && (!a.isControl || a.isTaxAccount)).map((a) => <option key={a.id} value={a.code}>{a.code} — {a.name}</option>)}
          </select>
        )}
        {sel.kind === 'ROLE' && (
          <select className={filterClass + ' w-full'} value={sel.role} onChange={(e) => onChange({ ...line, account: { kind: 'ROLE', role: e.target.value } })}>
            {SYSTEM_ROLES.map((r) => <option key={r} value={r}>{r}{accounts.find((a) => a.systemRole === r) ? ` → ${accounts.find((a) => a.systemRole === r)!.code}` : ''}</option>)}
          </select>
        )}
        {sel.kind === 'FIELD' && <input className={filterClass + ' w-full font-mono'} value={sel.expr} placeholder="payload.gl_code" onChange={(e) => onChange({ ...line, account: { kind: 'FIELD', expr: e.target.value } })} />}
        {sel.kind === 'MAPPING' && (
          <div className="flex flex-wrap gap-2">
            <select className={filterClass} value={sel.table} onChange={(e) => { const t = tables.find((x) => x.code === e.target.value); onChange({ ...line, account: { kind: 'MAPPING', table: e.target.value, keys: Object.fromEntries((t?.keyFields ?? []).map((k) => [k, sel.keys[k] ?? `item.${k}`])) } }); }}>
              {tables.map((t) => <option key={t.code} value={t.code}>{t.code}</option>)}
            </select>
            {Object.entries(sel.keys).map(([k, v]) => (
              <label key={k} className="flex items-center gap-1 text-xs"><code>{k} =</code><input className={filterClass + ' w-40 font-mono'} value={v} onChange={(e) => onChange({ ...line, account: { ...sel, keys: { ...sel.keys, [k]: e.target.value } } })} /></label>
            ))}
          </div>
        )}
      </div>
      <label className="md:col-span-12"><span className="text-xs font-medium text-slate-500">Amount expression</span>
        <input className={filterClass + ' w-full font-mono'} value={line.amount} placeholder="vat_base(gross_sales - vat_exempt_sales, VAT12)" onChange={(e) => onChange({ ...line, amount: e.target.value })} />
      </label>
      <label className="md:col-span-3"><span className="text-xs font-medium text-slate-500">For each</span>
        <input className={filterClass + ' w-full font-mono'} value={line.forEach ?? ''} placeholder="tenders" onChange={(e) => onChange({ ...line, forEach: e.target.value || undefined })} />
      </label>
      <label className="md:col-span-4"><span className="text-xs font-medium text-slate-500">Dimensions</span>
        <input className={filterClass + ' w-full font-mono'} defaultValue={dims} placeholder="BRANCH=branch" onBlur={(e) => onChange({ ...line, dimensions: Object.fromEntries(e.target.value.split(/[;,]/).map((p) => p.split('=').map((x) => x.trim())).filter((p) => p.length === 2 && p[0] && p[1])) })} />
      </label>
      <div className="flex items-end justify-end gap-1 md:col-span-5">
        {!readOnly && (
          <>
            <Button type="button" size="sm" variant="ghost" aria-label="Move up" onClick={() => onMove(-1)}><ArrowUp aria-hidden="true" className="h-4 w-4" /></Button>
            <Button type="button" size="sm" variant="ghost" aria-label="Move down" onClick={() => onMove(1)}><ArrowDown aria-hidden="true" className="h-4 w-4" /></Button>
            <Button type="button" size="sm" variant="ghost" aria-label="Remove line" onClick={onRemove}><Trash2 aria-hidden="true" className="h-4 w-4" /></Button>
          </>
        )}
      </div>
      <label className="md:col-span-12"><span className="text-xs font-medium text-slate-500">Description</span>
        <input className={filterClass + ' w-full'} value={line.description ?? ''} placeholder="{{item.method}} tenders {{event.business_date}}" onChange={(e) => onChange({ ...line, description: e.target.value })} />
      </label>
    </fieldset>
  );
}

const BLANK: LineTemplate[] = [
  { side: 'DEBIT', account: { kind: 'FIXED', code: '' }, amount: '' },
  { side: 'CREDIT', account: { kind: 'FIXED', code: '' }, amount: '' },
];

export function PostingRuleEditorPage(): JSX.Element {
  const { ruleId } = useParams();
  const isNew = ruleId === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, user } = useAuth();
  const rules = useRules();
  const types = useEventTypes();
  const accounts = useAccounts();
  const tablesQ = useMappingTables();
  const rule = rules.data?.find((r) => r.id === ruleId);
  const [versionId, setVersionId] = useState<string | null>(null);
  const version = rule?.versions.find((v) => v.id === versionId) ?? rule?.versions[0];

  const [name, setName] = useState('');
  const [eventType, setEventType] = useState('');
  const [priority, setPriority] = useState(100);
  const [effectiveFrom, setEffectiveFrom] = useState(manilaToday());
  const [conditions, setConditions] = useState<string[]>([]);
  const [lines, setLines] = useState<LineTemplate[]>(BLANK);
  const [sample, setSample] = useState('{\n}');
  const [simDate, setSimDate] = useState(manilaToday());
  const [sim, setSim] = useState<SimResult | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'critical'; text: string } | null>(null);
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    if (rule && version) {
      setName(rule.name);
      setEventType(rule.eventType);
      setPriority(rule.priority);
      setEffectiveFrom(version.effectiveFrom);
      setConditions(version.conditions);
      setLines(version.lines);
      setSim(null);
    }
  }, [rule, version]);

  const editable = isNew || version?.status === 'DRAFT';
  const tables = (tablesQ.data ?? []).map((t) => ({ code: t.code, keyFields: t.keyFields }));
  const refresh = () => void qc.invalidateQueries({ queryKey: ['posting-rules'] });

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) return api<{ id: string; versionId: string }>('/posting-rules', { method: 'POST', body: { eventType, name, priority, effectiveFrom, conditions, lines } });
      if (rule && (name !== rule.name || priority !== rule.priority)) await api(`/posting-rules/${rule.id}`, { method: 'PATCH', body: { name, priority } });
      await api(`/posting-rules/versions/${version!.id}`, { method: 'PATCH', body: { effectiveFrom, conditions, lines } });
      return { id: rule!.id, versionId: version!.id };
    },
    onSuccess: (r) => { refresh(); setMessage({ tone: 'ok', text: 'Draft saved. Test it before submitting.' }); if (isNew) navigate(`/integrations/rules/${r.id}`, { replace: true }); },
    onError: (e) => setMessage({ tone: 'critical', text: errorText(e) }),
  });

  const simulate = useMutation({
    mutationFn: () => {
      let payload: Record<string, unknown>;
      try { payload = JSON.parse(sample) as Record<string, unknown>; } catch { throw new Error('The sample payload is not valid JSON.'); }
      return api<SimResult>(`/posting-rules/versions/${version!.id}/simulate`, { method: 'POST', body: { payload, businessDate: simDate } });
    },
    onSuccess: (r) => { setSim(r); refresh(); },
    onError: (e) => setMessage({ tone: 'critical', text: errorText(e) }),
  });

  const step = useMutation({
    mutationFn: ({ action, body }: { action: string; body?: unknown }) => api(action === 'discard' ? `/posting-rules/versions/${version!.id}` : action === 'new-version' ? `/posting-rules/${rule!.id}/versions` : `/posting-rules/versions/${version!.id}/${action}`, { method: action === 'discard' ? 'DELETE' : 'POST', body }),
    onSuccess: (r, v) => {
      refresh();
      setRejecting(false);
      if (v.action === 'discard') { if (rule?.versions.length === 1) navigate('/integrations/rules'); else setVersionId(null); }
      if (v.action === 'new-version') setVersionId((r as { versionId: string }).versionId);
      setMessage({ tone: 'ok', text: { submit: 'Submitted for approval.', approve: 'Approved. Activate it to start posting.', activate: 'Active: events dated on or after the effective date now use this version.', retire: 'Retired.', reject: 'Sent back to the maker.', discard: 'Draft discarded.', 'new-version': 'New draft version created from the latest one.' }[v.action] ?? 'Done.' });
    },
    onError: (e) => setMessage({ tone: 'critical', text: errorText(e) }),
  });

  const mine = version && (version.createdById === user?.id || version.submittedById === user?.id);
  const type = types.data?.find((t) => t.code === eventType);

  useEffect(() => {
    // Seed the Test payload: the last sample used, else the schema's field names.
    const last = rule?.versions.find((v) => v.lastSimulation?.payload)?.lastSimulation?.payload;
    if (last && sample.trim() === '{\n}') {
      setSample(JSON.stringify(last, null, 2));
      return;
    }
    const props = (type?.schema as { properties?: Record<string, { type?: string }> } | undefined)?.properties;
    if (props && sample.trim() === '{\n}') setSample(JSON.stringify(Object.fromEntries(Object.entries(props).map(([k, v]) => [k, v.type === 'array' ? [] : v.type === 'number' ? 0 : ''])), null, 2));
  }, [type, rule?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => sim?.lines.reduce((s, l) => ({ dr: s.dr + Number(l.debit), cr: s.cr + Number(l.credit) }), { dr: 0, cr: 0 }), [sim]);

  if (!isNew && rules.isLoading) return <Skeleton className="h-64 w-full" />;
  if (!isNew && !rule) return <Alert tone="critical">Rule not found.</Alert>;

  return (
    <>
      <PageHeader
        title={isNew ? 'New posting rule' : rule!.name}
        description={isNew ? 'Lines are templates: an account selector, an amount expression and optional dimensions.' : `${rule!.eventType} · priority ${rule!.priority} (lower runs first; exactly one rule must match)`}
        actions={!isNew && version && (
          <div className="flex flex-wrap items-center gap-2">
            <RuleStatusChip status={version.status} />
            {version.status === 'DRAFT' && can('rules.edit') && <Button variant="secondary" busy={step.isPending} onClick={() => step.mutate({ action: 'submit' })}>Submit for approval</Button>}
            {version.status === 'DRAFT' && can('rules.edit') && <Button variant="ghost" onClick={() => { if (window.confirm('Discard this draft version?')) step.mutate({ action: 'discard' }); }}>Discard draft</Button>}
            {version.status === 'SUBMITTED' && can('rules.approve') && !mine && <Button busy={step.isPending} onClick={() => step.mutate({ action: 'approve' })}>Approve</Button>}
            {version.status === 'SUBMITTED' && can('rules.approve') && !mine && <Button variant="secondary" onClick={() => setRejecting(true)}>Send back</Button>}
            {version.status === 'APPROVED' && can('rules.approve') && <Button busy={step.isPending} onClick={() => step.mutate({ action: 'activate' })}>Activate</Button>}
            {(version.status === 'ACTIVE' || version.status === 'APPROVED') && can('rules.approve') && <Button variant="ghost" onClick={() => { if (window.confirm('Retire this version? Events after today will need another active version.')) step.mutate({ action: 'retire' }); }}>Retire</Button>}
            {!['DRAFT', 'SUBMITTED'].includes(rule!.versions[0].status) && can('rules.edit') && <Button variant="secondary" onClick={() => step.mutate({ action: 'new-version', body: { effectiveFrom: manilaToday() } })}>New version</Button>}
          </div>
        )}
      />
      {message && <div className="mb-4"><Alert tone={message.tone}>{message.text}</Alert></div>}
      {!isNew && rule!.versions.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="Versions">
          {rule!.versions.map((v) => (
            <button key={v.id} role="tab" aria-selected={v.id === version?.id} onClick={() => setVersionId(v.id)} className={`rounded-xl border px-3 py-1.5 text-sm ${v.id === version?.id ? 'border-brand bg-brand-50 font-semibold text-brand-700 dark:bg-slate-800 dark:text-brand-100' : 'border-slate-200 dark:border-slate-700'}`}>
              v{v.version} · {v.status.toLowerCase()} · {shortDate(v.effectiveFrom)}
            </button>
          ))}
        </div>
      )}
      {!editable && <div className="mb-4"><Alert tone="info">Version {version?.version} is {version?.status.toLowerCase()} and cannot change. Create a new version to edit; entries already posted are never re-derived.</Alert></div>}

      <div className="grid gap-4 xl:grid-cols-5">
        <div className="space-y-4 xl:col-span-3">
          <Card title="Rule">
            <fieldset disabled={!editable} className="grid gap-3 sm:grid-cols-2">
              <Field label="Name" required><input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} /></Field>
              <Field label="Event type" required>
                <select className={inputClass} value={eventType} onChange={(e) => setEventType(e.target.value)} disabled={!isNew}>
                  <option value="">Choose…</option>
                  {types.data?.filter((t) => t.active).map((t) => <option key={t.code} value={t.code}>{t.code}</option>)}
                </select>
              </Field>
              <Field label="Priority" hint="Lower numbers are tried first."><input className={inputClass} type="number" min={1} max={9999} value={priority} onChange={(e) => setPriority(Number(e.target.value))} /></Field>
              <Field label="Effective from" hint="Applies to events dated on or after this day."><input className={inputClass} type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} /></Field>
            </fieldset>
          </Card>

          <Card title="Conditions" description="All must be true for this rule to apply, e.g. branch == 'CEBU-01' or one_of(payment_method, 'GCASH', 'MAYA'). Leave empty to match every event.">
            <fieldset disabled={!editable} className="space-y-2">
              {conditions.map((c, i) => (
                <div key={i} className="flex gap-2">
                  <input className={inputClass + ' font-mono'} value={c} onChange={(e) => setConditions(conditions.map((x, j) => (j === i ? e.target.value : x)))} />
                  {editable && <Button type="button" variant="ghost" aria-label="Remove condition" onClick={() => setConditions(conditions.filter((_, j) => j !== i))}><Trash2 aria-hidden="true" className="h-4 w-4" /></Button>}
                </div>
              ))}
              {editable && <Button type="button" size="sm" variant="secondary" onClick={() => setConditions([...conditions, ''])}><Plus aria-hidden="true" className="h-4 w-4" /> Condition</Button>}
            </fieldset>
          </Card>

          <Card title="Lines" description="Tax lines must use vat_amount() or ewt() — the tax engine computes them. Control accounts are refused.">
            <div className="space-y-3">
              {lines.map((l, i) => (
                <LineEditor
                  key={i}
                  line={l}
                  accounts={accounts.data ?? []}
                  tables={tables}
                  readOnly={!editable}
                  onChange={(n) => setLines(lines.map((x, j) => (j === i ? n : x)))}
                  onRemove={() => setLines(lines.filter((_, j) => j !== i))}
                  onMove={(d) => { const j = i + d; if (j < 0 || j >= lines.length) return; const next = [...lines]; [next[i], next[j]] = [next[j], next[i]]; setLines(next); }}
                />
              ))}
              {editable && (
                <div className="flex flex-wrap justify-between gap-2">
                  <Button type="button" size="sm" variant="secondary" onClick={() => setLines([...lines, { side: 'CREDIT', account: { kind: 'FIXED', code: '' }, amount: '' }])}><Plus aria-hidden="true" className="h-4 w-4" /> Line</Button>
                  <Button busy={save.isPending} disabled={!name || !eventType} onClick={() => save.mutate()}>Save draft</Button>
                </div>
              )}
            </div>
            <details className="mt-4 text-sm text-slate-600 dark:text-slate-300">
              <summary className="cursor-pointer font-medium">Expression reference</summary>
              <ul className="mt-2 list-disc space-y-1 pl-5 font-mono text-xs">
                <li>gross_sales, payload.branch, item.amount, event.business_date</li>
                <li>+ − × ÷ ( ), == != &gt; &gt;= &lt; &lt;=, and, or, not</li>
                <li>round(x, 2) abs(x) min(a, b) max(a, b) if(cond, a, b) one_of(x, 'A', 'B')</li>
                <li>sum(tenders[].amount where method == 'CASH'), count(tenders[])</li>
                <li>vat_base(x, VAT12) vat_amount(x, VAT12) ewt(x, WC158)</li>
              </ul>
            </details>
          </Card>
        </div>

        <div className="xl:col-span-2">
          <Card title="Test" description="A dry run with a sample payload: the journal this version would post, checked like the real thing. At least one passing test is needed to submit." className="xl:sticky xl:top-4">
            {isNew ? <p className="text-sm text-slate-500">Save the draft first.</p> : (
              <div className="space-y-3">
                <Field label="Business date"><input className={inputClass} type="date" value={simDate} onChange={(e) => setSimDate(e.target.value)} /></Field>
                <Field label="Sample payload (JSON)"><textarea className={inputClass + ' font-mono text-xs'} rows={12} value={sample} onChange={(e) => setSample(e.target.value)} spellCheck={false} /></Field>
                <Button busy={simulate.isPending} onClick={() => simulate.mutate()}><FlaskConical aria-hidden="true" className="h-4 w-4" /> Run test</Button>
                {version?.lastSimulation && !sim && <p className="text-xs text-slate-500">Last test {version.lastSimulation.passed ? 'passed' : 'failed'} {dateTime(version.lastSimulation.createdAt)}.</p>}
                {sim && (
                  <div className="space-y-2">
                    <Alert tone={sim.passed ? 'ok' : 'critical'}>{sim.code ? <><code className="text-xs">{sim.code}</code> </> : null}{sim.message}{sim.errors?.length ? ` ${sim.errors.map((e) => `${e.field} ${e.message}`).join('; ')}` : ''}</Alert>
                    {sim.lines.length > 0 && (
                      <TableWrap>
                        <thead><tr><Th>Account</Th><Th align="right">Debit</Th><Th align="right">Credit</Th></tr></thead>
                        <tbody className="divide-y divide-slate-100 text-xs dark:divide-slate-800">
                          {sim.lines.map((l, i) => (
                            <tr key={i}>
                              <Td><span className="font-medium">{l.accountCode} {l.accountName}</span>{(l.description || Object.keys(l.dimensionLabels).length > 0) && <span className="block text-slate-500">{l.description} {Object.entries(l.dimensionLabels).map(([k, v]) => `${k}:${v}`).join(' ')}</span>}</Td>
                              <Td align="right" className="tabular-nums">{Number(l.debit) ? peso(l.debit) : ''}</Td>
                              <Td align="right" className="tabular-nums">{Number(l.credit) ? peso(l.credit) : ''}</Td>
                            </tr>
                          ))}
                          <tr className="font-semibold"><Td>Total</Td><Td align="right" className="tabular-nums">{peso(totals?.dr ?? 0)}</Td><Td align="right" className="tabular-nums">{peso(totals?.cr ?? 0)}</Td></tr>
                        </tbody>
                      </TableWrap>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>
      {!isNew && version && <p className="mt-4 text-xs text-slate-500">Lines in this version: {version.lines.map((l) => `${l.side === 'DEBIT' ? 'Dr' : 'Cr'} ${selectorLabel(l.account)}`).join(' · ')}</p>}
      {rejecting && <ReasonDialog title="Send back to the maker" confirmLabel="Send back" tone="primary" busy={step.isPending} onClose={() => setRejecting(false)} onConfirm={(reason) => step.mutate({ action: 'reject', body: { reason } })} />}
    </>
  );
}
